import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { bmxurl, getAccess } from 'src/globals';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';
import { cDaysheetresourceorder } from 'src/types/daysheetresourceorder';
import { DaysheetOrderService } from '../_services/daysheet-order.service';

//One row on the page: a resource or a resource group, in the one list the Daysheet is ordered by.
interface orderrow {
  propertyid: string;
  propertytype: string;
  name: string;
  showondaysheet: boolean;
}

/*
  Daysheet Resource Order - the order resources and resource groups appear in on the Daysheet,
  and whether each appears at all.

  Resources and resource groups come from their own lists, and are merged into one here so they
  can be ordered against each other. Saving sends the whole list to ResourceGroupResourceOrder,
  one line per resource or group, numbered from 0 in the order shown.
*/
@Component({
  selector: 'app-daysheetresourceorder',
  templateUrl: './daysheetresourceorder.component.html',
  styleUrls: ['./daysheetresourceorder.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DaysheetresourceorderComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;

  rows: orderrow[] = [];

  //Whether anything has changed since it was loaded or saved, so Save can say so.
  changed: boolean = false;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private globalService: GlobalService,
    private busyService: BusyService,
    private daysheetOrder: DaysheetOrderService) { }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    const userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      const result = response as any;

      if (result.succeeded) {
        //The same access the Resources screen asks for.
        this.AccessAllowed = getAccess("RESOURCES", result.data) == true;

        if (this.AccessAllowed) {
          this.load();
        }
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  //Read through the same service the Daysheet uses, so this page always shows the order the
  //Daysheet will actually follow.
  load() {
    this.busyService.busy();

    this.daysheetOrder.load(this.VenueID).subscribe(result => {
      if (result.failed) {
        this.messageService.add({severity:'error', summary:'Error', detail:'The resources or resource groups could not be loaded.'});
      }

      this.rows = result.items.map(item => ({
        propertyid: item.propertyid,
        propertytype: item.propertytype,
        name: item.name,
        showondaysheet: item.showondaysheet
      }));

      this.changed = false;
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error', detail:'The resources or resource groups could not be loaded.'});
    });
  }

  //Dragging a row (on a desktop) has already moved it - the table does that - so this only notes it.
  onRowReorder() {
    this.changed = true;
  }

  //The up and down buttons, which work on a phone where rows cannot be dragged.
  move(at: number, by: number) {
    const to = at + by;

    if (to < 0 || to >= this.rows.length) {
      return;
    }

    const moved = this.rows.splice(at, 1)[0];
    this.rows.splice(to, 0, moved);
    this.rows = [...this.rows];
    this.changed = true;
  }

  toggled() {
    this.changed = true;
  }

  save() {
    const order: cDaysheetresourceorder[] = this.rows.map((row, at) => ({
      venueid: this.VenueID,
      propertyid: row.propertyid,
      propertytype: row.propertytype,
      orderindex: at,
      showondaysheet: row.showondaysheet == true
    }));

    this.busyService.busy();

    this.http.post<any>(bmxurl + "ResourceGroupResourceOrder", order).subscribe(response => {
      this.busyService.idle();

      if (response?.succeeded == false) {
        this.messageService.add({severity:'error', summary:'Not Saved', detail: response.messages?.[0] ?? 'The Daysheet order could not be saved.'});
        return;
      }

      this.changed = false;
      this.messageService.add({severity:'success', summary:'Saved', detail:'Daysheet order saved'});
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Not Saved', detail:'The Daysheet order could not be saved.'});
    });
  }
}
